import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useStore } from '../lib/store';

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

interface Member {
  id: string;
  displayName: string;
  role: 'member' | 'admin' | 'owner' | 'super';
  email: string | null;
  disabled: boolean;
  createdAt: number;
}

type Tab = 'members' | 'cove';

export function Admin() {
  const user = useStore((s) => s.user);
  const cove = useStore((s) => s.cove);
  const [tab, setTab] = useState<Tab>('members');

  if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="app">
      <header className="chat-header" style={{ justifyContent: 'space-between' }}>
        <Link to="/settings" className="back" aria-label="Back">
          <BackIcon />
        </Link>
        <h1 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-md)', fontWeight: 700, margin: 0 }}>
          {cove?.name ?? 'Cove admin'}
        </h1>
        <div style={{ width: 22 }} />
      </header>

      <div className="tab-strip">
        <button className={tab === 'members' ? 'on' : ''} onClick={() => setTab('members')}>Members</button>
        <button className={tab === 'cove' ? 'on' : ''} onClick={() => setTab('cove')}>Cove</button>
      </div>

      {tab === 'members' ? <MembersTab /> : <CoveTab />}
    </div>
  );
}

function MembersTab() {
  const user = useStore((s) => s.user)!;
  const cove = useStore((s) => s.cove)!;
  const [members, setMembers] = useState<Member[]>([]);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [prompt, setPrompt] = useState<{ label: string; run: (pw: string) => Promise<void> } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const r = await api.adminMembers();
      setMembers(r);
    } catch (e: any) {
      setErr(e.message);
    }
  }
  useEffect(() => { refresh(); }, []);

  function promote(m: Member) {
    setPrompt({
      label: `Promote ${m.displayName} to admin — confirm your password.`,
      run: async (pw) => {
        await api.adminPromote(m.id, pw);
        await refresh();
      },
    });
  }
  function demote(m: Member) {
    setPrompt({
      label: `Demote ${m.displayName} to member — confirm your password.`,
      run: async (pw) => {
        await api.adminDemote(m.id, pw);
        await refresh();
      },
    });
  }
  function transfer(m: Member) {
    setPrompt({
      label: `Offer ownership to ${m.displayName} — confirm your password. They have 24h to accept.`,
      run: async (pw) => {
        await api.adminTransferOwnership(m.id, pw);
        await refresh();
      },
    });
  }
  function toggleDisabled(m: Member) {
    setPrompt({
      label: `${m.disabled ? 'Enable' : 'Disable'} ${m.displayName} — confirm your password.`,
      run: async (pw) => {
        if (m.disabled) await api.adminEnable(m.id, pw);
        else await api.adminDisable(m.id, pw);
        await refresh();
      },
    });
  }

  return (
    <div style={{ padding: 'var(--s-5)' }}>
      {err && <div className="error">{err}</div>}
      <ul className="settings-group">
        {members.map((m) => (
          <li key={m.id} className="settings-row" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>
                {m.displayName}
                {m.disabled && <span className="pill" style={{ marginLeft: 8 }}>disabled</span>}
              </div>
              <div className="muted small">
                {m.role === 'owner' ? 'Owner' : m.role === 'admin' ? 'Admin' : 'Member'}
                {m.email && ` · ${m.email}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {m.role === 'member' && (
                <button className="btn secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 13 }} onClick={() => promote(m)}>
                  Promote
                </button>
              )}
              {m.role === 'admin' && user.role === 'owner' && m.id !== user.id && (
                <>
                  <button className="btn secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 13 }} onClick={() => demote(m)}>
                    Demote
                  </button>
                  <button className="btn secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 13 }} onClick={() => transfer(m)}>
                    Transfer
                  </button>
                </>
              )}
              {m.role !== 'owner' && m.id !== user.id && (
                <button className="btn secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 13 }} onClick={() => toggleDisabled(m)}>
                  {m.disabled ? 'Enable' : 'Disable'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <button className="btn block" style={{ marginTop: 'var(--s-5)' }} onClick={() => setAddChildOpen(true)}>
        Add a child
      </button>

      {addChildOpen && (
        <AddChildSheet
          onClose={() => setAddChildOpen(false)}
          onDone={() => { setAddChildOpen(false); refresh(); }}
        />
      )}
      {prompt && (
        <PasswordPromptSheet
          label={prompt.label}
          onClose={() => setPrompt(null)}
          onSubmit={async (pw) => {
            try {
              await prompt.run(pw);
              setPrompt(null);
            } catch (e: any) {
              setErr(e.message || 'action failed');
              setPrompt(null);
            }
          }}
        />
      )}

      <div className="muted small" style={{ marginTop: 'var(--s-6)' }}>
        Cove: <code>{cove.id}</code>
      </div>
    </div>
  );
}

function CoveTab() {
  const cove = useStore((s) => s.cove)!;
  const user = useStore((s) => s.user)!;
  const nav = useNavigate();
  const [name, setName] = useState(cove.name);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function rename() {
    setBusy(true);
    setMsg(null);
    try {
      await api.renameCove(name.trim());
      setMsg('Saved.');
    } catch (e: any) {
      setMsg(e.message || 'rename failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 'var(--s-5)' }}>
      <label className="field">
        <span className="field-label">Cove name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={user.role !== 'owner'} />
      </label>
      <label className="field">
        <span className="field-label">Plan</span>
        <input className="input" value={cove.plan} readOnly />
      </label>
      {msg && <div className="muted small">{msg}</div>}
      {user.role === 'owner' && (
        <>
          <button className="btn block" onClick={rename} disabled={busy || name.trim() === cove.name || name.trim().length === 0}>
            {busy ? 'Saving…' : 'Save cove name'}
          </button>
          <button
            className="btn block"
            style={{ background: 'var(--danger)', marginTop: 'var(--s-6)' }}
            onClick={() => setDeleteOpen(true)}
          >
            Delete this cove…
          </button>
        </>
      )}
      {deleteOpen && (
        <DeleteCoveSheet
          coveName={cove.name}
          onClose={() => setDeleteOpen(false)}
          onDone={() => nav('/login', { replace: true })}
        />
      )}
    </div>
  );
}

function PasswordPromptSheet({ label, onClose, onSubmit }: { label: string; onClose: () => void; onSubmit: (pw: string) => Promise<void> }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <p style={{ margin: 0 }}>{label}</p>
        <label className="field">
          <span className="field-label">Password</span>
          <input
            className="input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            autoComplete="current-password"
          />
        </label>
        <button className="btn block" disabled={busy || pw.length < 6} onClick={async () => {
          setBusy(true);
          await onSubmit(pw);
          setBusy(false);
        }}>
          {busy ? 'Working…' : 'Confirm'}
        </button>
        <button className="btn secondary block" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function AddChildSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <h3 className="display" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Add a child</h3>
        <p className="muted small" style={{ marginTop: 0 }}>
          No email required for kids. Tell them the password verbally.
        </p>
        <label className="field">
          <span className="field-label">Their name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span className="field-label">Shared password</span>
          <input className="input" value={pw} onChange={(e) => setPw(e.target.value)} />
        </label>
        {err && <div className="error">{err}</div>}
        <button className="btn block" disabled={busy || !name.trim() || pw.length < 6} onClick={async () => {
          setBusy(true);
          try {
            await api.addChild(name.trim(), pw);
            onDone();
          } catch (e: any) {
            setErr(e.message || 'failed');
          } finally {
            setBusy(false);
          }
        }}>
          {busy ? 'Adding…' : 'Add child'}
        </button>
        <button className="btn secondary block" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function DeleteCoveSheet({ coveName, onClose, onDone }: { coveName: string; onClose: () => void; onDone: () => void }) {
  const [confirmName, setConfirmName] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <h3 className="display" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Delete this cove?</h3>
        <p className="muted small">
          30-day grace period before permanent purge. Every member loses access when you confirm.
        </p>
        <label className="field">
          <span className="field-label">Type the cove name to confirm</span>
          <input className="input" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={coveName} />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </label>
        {err && <div className="error">{err}</div>}
        <button
          className="btn block"
          style={{ background: 'var(--danger)' }}
          disabled={busy || confirmName !== coveName || pw.length < 6}
          onClick={async () => {
            setBusy(true);
            try {
              await api.deleteCove(confirmName, pw);
              onDone();
            } catch (e: any) {
              setErr(e.message || 'failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Deleting…' : 'Delete cove'}
        </button>
        <button className="btn secondary block" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
